import { UserInputError } from "./errors-CEDWDXew.js";
import { ParamOrganizationSlug, ParamRegionUrl } from "./schema-DKEjeVqP.js";
import { defineTool } from "./define-SD6AQa4b.js";
import { apiServiceFromContext } from "./api-EaEPyKlQ.js";
import { structuredResult } from "./results-NF1RJbDZ.js";
import { z } from "zod";
import { setTag } from "@sentry/core";

//#region src/tools/catalog/get-agent-conversation-details.ts
function withoutUndefined(value) {
	return Object.fromEntries(Object.entries(value).filter(([, item]) => item !== void 0));
}
const genAiAttributesSchema = z.object({
	operationType: z.string().optional(),
	agentName: z.string().optional(),
	requestModel: z.string().optional(),
	responseModel: z.string().optional(),
	usageTotalTokens: z.number().optional(),
	costTotalTokens: z.number().optional(),
	systemInstructions: z.string().optional(),
	inputMessages: z.string().optional(),
	outputMessages: z.string().optional(),
	requestMessages: z.string().optional(),
	responseText: z.string().optional(),
	responseObject: z.string().optional(),
	toolDefinitions: z.string().optional(),
	toolName: z.string().optional(),
	toolCallArguments: z.string().optional(),
	toolInput: z.string().optional(),
	truncatedFields: z.array(z.string()).optional()
});
const toolCallGenAiAttributesSchema = genAiAttributesSchema.omit({
	toolName: true,
	toolCallArguments: true,
	toolInput: true,
	truncatedFields: true
});
const toolCallSchema = z.object({
	type: z.literal("tool_call"),
	name: z.string(),
	spanId: z.string(),
	traceId: z.string(),
	timestamp: z.number(),
	durationMs: z.number(),
	status: z.string().optional(),
	arguments: z.string().optional(),
	input: z.string().optional(),
	genAi: toolCallGenAiAttributesSchema.optional()
});
const conversationMessageSchema = z.object({
	type: z.literal("message"),
	role: z.enum(["user", "assistant"]),
	content: z.string(),
	timestamp: z.number(),
	spanId: z.string(),
	traceId: z.string(),
	userEmail: z.string().optional(),
	metadata: z.object({
		agentName: z.string().optional(),
		model: z.string().optional(),
		totalTokens: z.number(),
		status: z.string().optional(),
		durationMs: z.number()
	}).optional(),
	genAi: genAiAttributesSchema.optional()
});
const timelineEventSchema = z.discriminatedUnion("type", [conversationMessageSchema, toolCallSchema]);
const lookupWindowSchema = z.object({
	statsPeriod: z.string().optional(),
	start: z.string().optional(),
	end: z.string().optional()
});
const spanSummarySchema = z.object({
	spanId: z.string(),
	traceId: z.string(),
	parentSpanId: z.string().nullable().optional(),
	project: z.string(),
	projectId: z.union([z.string(), z.number()]),
	timestamp: z.number(),
	durationMs: z.number(),
	spanName: z.string().optional(),
	spanOp: z.string().optional(),
	spanDescription: z.string().optional(),
	spanStatus: z.string().optional(),
	transaction: z.string().optional(),
	isTransaction: z.boolean().optional(),
	genAi: genAiAttributesSchema.optional()
});
const aiConversationDetailsOutputSchema = z.object({
	conversationId: z.string(),
	title: z.string().nullable(),
	organizationSlug: z.string(),
	url: z.string().url(),
	lookupWindow: lookupWindowSchema,
	startTimestamp: z.number().nullable(),
	endTimestamp: z.number().nullable(),
	traceIds: z.array(z.string()),
	projects: z.array(z.string()),
	spanCount: z.number(),
	aiCallCount: z.number(),
	messageCount: z.number(),
	toolCallCount: z.number(),
	totalTokens: z.number(),
	spanSummaries: z.array(spanSummarySchema),
	timeline: z.array(timelineEventSchema)
});
function numeric(value) {
	if (typeof value === "number") return value;
	if (typeof value === "string") {
		const parsed = Number(value);
		return Number.isFinite(parsed) ? parsed : 0;
	}
	return 0;
}
function optionalNumeric(value) {
	if (typeof value === "number") return value;
	if (typeof value === "string") {
		const parsed = Number(value);
		return Number.isFinite(parsed) ? parsed : void 0;
	}
	return void 0;
}
function timestampMs(value) {
	return Math.round(value * 1e3);
}
function durationMs(span) {
	return Math.round((span["precise.finish_ts"] - span["precise.start_ts"]) * 1e3);
}
const MAX_RAW_GEN_AI_ATTRIBUTE_LENGTH = 4e3;
function truncateAttribute(field, value, truncatedFields) {
	if (value === void 0 || value.length <= MAX_RAW_GEN_AI_ATTRIBUTE_LENGTH) return value;
	truncatedFields.push(field);
	return `${value.slice(0, MAX_RAW_GEN_AI_ATTRIBUTE_LENGTH)}... [truncated]`;
}
function getOperationType(span) {
	const explicit = span["gen_ai.operation.type"];
	if (explicit) return explicit;
	const spanName = span["span.name"];
	if (!spanName?.startsWith("gen_ai.")) return void 0;
	if (spanName === "gen_ai.execute_tool") return "tool";
	if (spanName === "gen_ai.invoke_agent" || spanName === "gen_ai.create_agent") return "agent";
	if (spanName === "gen_ai.handoff") return "handoff";
	return "ai_client";
}
function parseJson(value) {
	try {
		return JSON.parse(value);
	} catch {
		return value;
	}
}
function stringifyContent(value) {
	if (typeof value === "string") return value;
	if (Array.isArray(value)) {
		const parts = value.map((part) => {
			if (typeof part === "string") return part;
			if (part && typeof part === "object") {
				const record = part;
				if (typeof record.text === "string") return record.text;
				if (typeof record.content === "string") return record.content;
			}
			return null;
		}).filter((part) => Boolean(part));
		return parts.length > 0 ? parts.join("\n") : null;
	}
	if (value && typeof value === "object") {
		const record = value;
		if (typeof record.text === "string") return record.text;
		if (typeof record.content === "string") return record.content;
		if (typeof record.message === "string") return record.message;
	}
	return value == null ? null : JSON.stringify(value);
}
function collectMessages(value) {
	const source = value && typeof value === "object" && !Array.isArray(value) && Array.isArray(value.messages) ? value.messages : value;
	if (!Array.isArray(source)) {
		const content = stringifyContent(source);
		return content ? [{ content }] : [];
	}
	return source.map((message) => {
		if (typeof message === "string") return { content: message };
		if (!message || typeof message !== "object") return null;
		const record = message;
		const content = stringifyContent(record.content ?? record.text ?? record);
		if (!content) return null;
		return {
			role: typeof record.role === "string" ? record.role : void 0,
			content
		};
	}).filter((message) => Boolean(message));
}
function extractUserContent(span) {
	const raw = span["gen_ai.input.messages"] ?? span["gen_ai.request.messages"] ?? null;
	if (!raw) return null;
	if (raw === "[Filtered]") return raw;
	const messages = collectMessages(parseJson(raw));
	const userMessage = messages.findLast((message) => message.role === "user");
	return userMessage?.content ?? messages.at(-1)?.content ?? null;
}
function extractAssistantContent(span) {
	const outputMessages = span["gen_ai.output.messages"];
	if (outputMessages) {
		if (outputMessages === "[Filtered]") return outputMessages;
		const messages = collectMessages(parseJson(outputMessages));
		const assistantMessage = messages.findLast((message) => message.role === "assistant");
		const content = assistantMessage?.content ?? messages.at(-1)?.content;
		if (content) return content;
	}
	return span["gen_ai.response.text"] ?? span["gen_ai.response.object"] ?? null;
}
/** Projects documented GenAI fields into the MCP contract and marks truncated raw payloads. */
function buildGenAIAttributes(span) {
	const truncatedFields = [];
	const attributes = withoutUndefined({
		operationType: getOperationType(span),
		agentName: span["gen_ai.agent.name"],
		requestModel: span["gen_ai.request.model"],
		responseModel: span["gen_ai.response.model"],
		usageTotalTokens: optionalNumeric(span["gen_ai.usage.total_tokens"]),
		costTotalTokens: optionalNumeric(span["gen_ai.cost.total_tokens"]),
		systemInstructions: truncateAttribute("systemInstructions", span["gen_ai.system_instructions"], truncatedFields),
		inputMessages: truncateAttribute("inputMessages", span["gen_ai.input.messages"], truncatedFields),
		outputMessages: truncateAttribute("outputMessages", span["gen_ai.output.messages"], truncatedFields),
		requestMessages: truncateAttribute("requestMessages", span["gen_ai.request.messages"], truncatedFields),
		responseText: truncateAttribute("responseText", span["gen_ai.response.text"], truncatedFields),
		responseObject: truncateAttribute("responseObject", span["gen_ai.response.object"], truncatedFields),
		toolDefinitions: truncateAttribute("toolDefinitions", span["gen_ai.tool.definitions"], truncatedFields),
		toolName: span["gen_ai.tool.name"],
		toolCallArguments: truncateAttribute("toolCallArguments", span["gen_ai.tool.call.arguments"], truncatedFields),
		toolInput: truncateAttribute("toolInput", span["gen_ai.tool.input"], truncatedFields),
		truncatedFields: truncatedFields.length > 0 ? truncatedFields.sort() : void 0
	});
	return Object.keys(attributes).length > 0 ? attributes : void 0;
}
/** Builds the MCP-facing span summary with IDs for raw span follow-up. */
function buildSpanSummary(span) {
	return withoutUndefined({
		spanId: span.span_id,
		traceId: span.trace,
		parentSpanId: span.parent_span,
		project: span.project,
		projectId: span["project.id"],
		timestamp: timestampMs(span["precise.start_ts"]),
		durationMs: durationMs(span),
		spanName: span["span.name"],
		spanOp: span["span.op"],
		spanDescription: span["span.description"],
		spanStatus: span["span.status"],
		transaction: span.transaction,
		isTransaction: span.is_transaction,
		genAi: buildGenAIAttributes(span)
	});
}
function buildToolCallGenAIAttributes(attributes) {
	if (!attributes) return void 0;
	const { toolName: _toolName, toolCallArguments: _toolCallArguments, toolInput: _toolInput, truncatedFields: _truncatedFields,...timelineAttributes } = attributes;
	return Object.keys(timelineAttributes).length > 0 ? timelineAttributes : void 0;
}
function buildToolCall(span) {
	const name = span["gen_ai.tool.name"];
	if (!name) return null;
	const genAi = buildGenAIAttributes(span);
	return withoutUndefined({
		type: "tool_call",
		name,
		spanId: span.span_id,
		traceId: span.trace,
		timestamp: timestampMs(span["precise.start_ts"]),
		durationMs: durationMs(span),
		status: span["span.status"],
		arguments: genAi?.toolCallArguments,
		input: genAi?.toolInput,
		genAi: buildToolCallGenAIAttributes(genAi)
	});
}
function buildMessageEvents(span) {
	const spanDurationMs = durationMs(span);
	const genAi = buildGenAIAttributes(span);
	const metadata = withoutUndefined({
		agentName: span["gen_ai.agent.name"],
		model: span["gen_ai.response.model"] ?? span["gen_ai.request.model"],
		totalTokens: numeric(span["gen_ai.usage.total_tokens"]),
		status: span["span.status"],
		durationMs: spanDurationMs
	});
	const events = [];
	const userContent = extractUserContent(span);
	if (userContent) events.push(withoutUndefined({
		type: "message",
		role: "user",
		content: userContent,
		timestamp: timestampMs(span["precise.start_ts"]),
		spanId: span.span_id,
		traceId: span.trace,
		userEmail: span["user.email"],
		metadata: void 0,
		genAi
	}));
	const assistantContent = extractAssistantContent(span);
	if (assistantContent) events.push({
		type: "message",
		role: "assistant",
		content: assistantContent,
		timestamp: timestampMs(span["precise.finish_ts"]),
		spanId: span.span_id,
		traceId: span.trace,
		metadata,
		genAi
	});
	else if (events.length > 0) events[events.length - 1] = {
		...events[events.length - 1],
		metadata,
		genAi
	};
	return events;
}
function extractTimeline(spans) {
	const events = [];
	for (const span of spans) {
		const operationType = getOperationType(span);
		if (operationType === "tool") {
			const toolCall = buildToolCall(span);
			if (toolCall) events.push(toolCall);
			continue;
		}
		if (operationType === "ai_client") events.push(...buildMessageEvents(span));
	}
	return events.sort((a, b) => {
		const timestampDiff = a.timestamp - b.timestamp;
		if (timestampDiff !== 0) return timestampDiff;
		if (a.type !== b.type) return a.type === "message" ? -1 : 1;
		return a.spanId.localeCompare(b.spanId);
	});
}
function countAICalls(spans) {
	return spans.filter((span) => getOperationType(span) === "ai_client").length;
}
/** Builds the stable structured artifact returned as conversation details. */
function buildConversationArtifact(apiService, organizationSlug, conversationId, title, spans, lookupWindow) {
	const timeline = extractTimeline(spans);
	const spanSummaries = spans.map(buildSpanSummary).sort((a, b) => {
		const timestampDiff = a.timestamp - b.timestamp;
		if (timestampDiff !== 0) return timestampDiff;
		return a.spanId.localeCompare(b.spanId);
	});
	const traceIds = [...new Set(spans.map((span) => span.trace))].sort();
	const projects = [...new Set(spans.map((span) => span.project))].sort();
	const startTimestamp = spans.length > 0 ? Math.min(...spans.map((span) => span["precise.start_ts"])) : null;
	const endTimestamp = spans.length > 0 ? Math.max(...spans.map((span) => span["precise.finish_ts"])) : null;
	return withoutUndefined({
		conversationId,
		title,
		organizationSlug,
		url: apiService.getAIConversationUrl(organizationSlug, conversationId),
		lookupWindow,
		startTimestamp: startTimestamp === null ? null : timestampMs(startTimestamp),
		endTimestamp: endTimestamp === null ? null : timestampMs(endTimestamp),
		traceIds,
		projects,
		spanCount: spans.length,
		aiCallCount: countAICalls(spans),
		messageCount: timeline.filter((event) => event.type === "message").length,
		toolCallCount: timeline.filter((event) => event.type === "tool_call").length,
		totalTokens: spans.reduce((sum, span) => sum + numeric(span["gen_ai.usage.total_tokens"]), 0),
		spanSummaries,
		timeline
	});
}
var get_agent_conversation_details_default = defineTool({
	name: "get_agent_conversation_details",
	skills: [
		"inspect",
		"triage",
		"seer"
	],
	requiredScopes: ["event:read", "project:read"],
	description: [
		"Fetch the chronological transcript and debugging details for one agent conversation, formerly called an AI conversation.",
		"",
		"Returns a timeline of user messages, assistant messages, and tool calls, with trace/span IDs for deeper debugging. To discover or list conversations, use search_agent_conversations."
	].join("\n"),
	inputSchema: {
		organizationSlug: ParamOrganizationSlug,
		conversationId: z.string().trim().describe("The agent conversation ID from gen_ai.conversation.id."),
		project: z.string().trim().optional().describe("Numeric project ID to scope the query. Falls back to context constraint or all projects."),
		start: z.string().trim().optional().describe("Explicit start time for the conversation lookup window."),
		end: z.string().trim().optional().describe("Explicit end time for the conversation lookup window."),
		regionUrl: ParamRegionUrl.optional()
	},
	annotations: {
		readOnlyHint: true,
		destructiveHint: false,
		openWorldHint: true
	},
	outputSchema: aiConversationDetailsOutputSchema,
	async handler(params, context) {
		setTag("organization.slug", params.organizationSlug);
		setTag("ai_conversation.id", params.conversationId);
		if (params.start && !params.end || !params.start && params.end) throw new UserInputError("`start` and `end` must be provided together.");
		const apiService = apiServiceFromContext(context, { regionUrl: params.regionUrl ?? void 0 });
		let projectId;
		if (context.constraints.projectSlug) {
			const constrainedProject = await apiService.getProject({
				organizationSlug: params.organizationSlug,
				projectSlugOrId: context.constraints.projectSlug
			});
			projectId = String(constrainedProject.id);
		} else if (params.project) projectId = params.project;
		const conversation = await apiService.getAIConversation({
			organizationSlug: params.organizationSlug,
			conversationId: params.conversationId,
			project: projectId ?? "-1",
			start: params.start,
			end: params.end
		}, void 0);
		const artifact = buildConversationArtifact(apiService, params.organizationSlug, params.conversationId, conversation.title, conversation.spans, params.start && params.end ? {
			start: params.start,
			end: params.end
		} : { statsPeriod: "30d" });
		return structuredResult(artifact);
	}
});

//#endregion
export { aiConversationDetailsOutputSchema, get_agent_conversation_details_default };
//# sourceMappingURL=get-agent-conversation-details-DTJR8hFg.js.map